const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

class ApiService {
  static getHeaders() {
    return { 'Content-Type': 'application/json' };
  }

  /**
   * Jedina tačka kroz koju idu API pozivi.
   *
   * Tokeni su u `HttpOnly` kolačićima, pa JavaScript ne može (i ne treba) da
   * ih čita — zato `credentials: 'include'`. `X-Requested-With` je CSRF
   * barijera na backend-u: kolačić browser šalje automatski, a obična forma
   * ne može da postavi custom header.
   *
   * Na 401 jednom pokušava refresh i ponavlja originalni zahtev.
   */
  static async request(url, { skipAuthRefresh = false, ...init } = {}) {
    const options = {
      credentials: 'include',
      ...init,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        ...(init.headers || {}),
      },
    };

    let response = await this.request(url, options);

    if (response.status === 401 && !skipAuthRefresh) {
      const refreshed = await this.refreshAuth();
      if (!refreshed) {
        // Bez ovog uslova bi provera sesije na `/login` strani pravila
        // beskonačno osvežavanje (401 -> redirect na /login -> 401 ...).
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        throw new Error('Session expired');
      }
      response = await fetch(url, options);
    }

    return response;
  }

  static async handleResponse(response) {
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }
    if (response.status === 204) return null;
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) return response;
    return response.json();
  }

  /**
   * Refresh ide sirovim `fetch`-om da ne bi ušao u rekurziju sa `request`.
   * Refresh token je u kolačiću sa `Path=/api/v1/auth`.
   */
  static async refreshAuth() {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  // ==================== AUTH ====================

  static async login(email, password) {
    const response = await this.request(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      skipAuthRefresh: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    return this.handleResponse(response);
  }

  static async register(userData) {
    const response = await this.request(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      skipAuthRefresh: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });
    return this.handleResponse(response);
  }

  static async logout() {
    const response = await this.request(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      skipAuthRefresh: true,
    });
    return this.handleResponse(response);
  }

  static async getCurrentUser() {
    const response = await this.request(`${API_BASE_URL}/users/me`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  // ==================== HA TERMINALS ====================

  static async listTerminals(params = {}) {
    const query = new URLSearchParams(params).toString();
    const response = await this.request(`${API_BASE_URL}/ha-terminals?${query}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  static async getTerminal(id) {
    const response = await this.request(`${API_BASE_URL}/ha-terminals/${id}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  static async createTerminal(data) {
    const response = await this.request(`${API_BASE_URL}/ha-terminals`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  static async assignTerminal(id, assignedTo) {
    const response = await this.request(`${API_BASE_URL}/ha-terminals/${id}/assign`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify({ assigned_to: assignedTo }),
    });
    return this.handleResponse(response);
  }

  static async startTerminal(id) {
    const response = await this.request(`${API_BASE_URL}/ha-terminals/${id}/start`, {
      method: 'PUT',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  static async completeTerminal(id) {
    const response = await this.request(`${API_BASE_URL}/ha-terminals/${id}/complete`, {
      method: 'PUT',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  static async getTerminalStats(projectCode) {
    const response = await this.request(`${API_BASE_URL}/ha-terminals/stats/dashboard?project_code=${projectCode}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  // ==================== PHOTOS ====================

  static async uploadPhoto(formData) {
    // Ne postavljati `Content-Type`: browser mora sam da doda multipart boundary.
    const response = await this.request(`${API_BASE_URL}/ha-photos/upload`, {
      method: 'POST',
      body: formData,
    });
    return this.handleResponse(response);
  }

  static async getPhoto(id) {
    const response = await this.request(`${API_BASE_URL}/ha-photos/${id}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  static async downloadPhoto(id) {
    return `${API_BASE_URL}/ha-photos/${id}/download`;
  }

  static async listTerminalPhotos(terminalId) {
    const response = await this.request(`${API_BASE_URL}/ha-photos/by-terminal/${terminalId}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  static async deletePhoto(id) {
    const response = await this.request(`${API_BASE_URL}/ha-photos/${id}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  // ==================== QA ====================

  static async getQAPendingTerminals(projectCode) {
    const query = projectCode ? `?project_code=${projectCode}` : '';
    const response = await this.request(`${API_BASE_URL}/qa/pending-terminals${query}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  static async getQAPendingPhotos(terminalId) {
    const response = await this.request(`${API_BASE_URL}/qa/pending-photos/${terminalId}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  static async approvePhoto(id) {
    const response = await this.request(`${API_BASE_URL}/qa/approve-photo/${id}`, {
      method: 'PUT',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  static async rejectPhoto(id, data) {
    const response = await this.request(`${API_BASE_URL}/qa/reject-photo/${id}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  static async approveTerminal(terminalId, data) {
    const response = await this.request(`${API_BASE_URL}/qa/approve-terminal/${terminalId}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  static async rejectTerminalQA(terminalId, reason) {
    const response = await this.request(`${API_BASE_URL}/qa/reject-terminal/${terminalId}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify({ reason }),
    });
    return this.handleResponse(response);
  }

  static async getQAStats(projectCode) {
    const query = projectCode ? `?project_code=${projectCode}` : '';
    const response = await this.request(`${API_BASE_URL}/qa/stats${query}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  static async getQAHistory(terminalId) {
    const response = await this.request(`${API_BASE_URL}/qa/history/${terminalId}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  // ==================== EXPORT ====================

  static async exportZIP(terminalIds) {
    const response = await this.request(`${API_BASE_URL}/export/zip`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ terminal_ids: terminalIds }),
    });
    return this.handleResponse(response);
  }

  static async getExportJobStatus(jobId) {
    const response = await this.request(`${API_BASE_URL}/export/job/${jobId}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  static async exportTerminalAsPDF(terminalId) {
    const response = await this.request(`${API_BASE_URL}/export/pdf/${terminalId}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  // ==================== NOTIFICATIONS ====================

  static async getNotifications() {
    const response = await this.request(`${API_BASE_URL}/notifications`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  static async markNotificationRead(id) {
    const response = await this.request(`${API_BASE_URL}/notifications/${id}/read`, {
      method: 'PUT',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  // ==================== FIELD REPORTS ====================

  static async createOrUpdateFieldReport(data) {
    const response = await this.request(`${API_BASE_URL}/ha-field-reports`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  static async getFieldReportByTerminal(terminalId) {
    const response = await this.request(`${API_BASE_URL}/ha-field-reports/terminal/${terminalId}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  static async listMyFieldReports() {
    const response = await this.request(`${API_BASE_URL}/ha-field-reports/my`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  // ==================== LOCATIONS ====================

  static async recordLocation(lat, lng, accuracy) {
    const response = await this.request(`${API_BASE_URL}/locations`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ latitude: lat, longitude: lng, accuracy }),
    });
    return this.handleResponse(response);
  }

  static async getUserLocations(userId) {
    const response = await this.request(`${API_BASE_URL}/locations/user/${userId}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  // ==================== USERS ====================

  static async listUsers() {
    const response = await this.request(`${API_BASE_URL}/users`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  static async updateProfile(data) {
    const response = await this.request(`${API_BASE_URL}/users/me`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  static async updateUserRole(userId, role) {
    const response = await this.request(`${API_BASE_URL}/users/role`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify({ user_id: userId, role }),
    });
    return this.handleResponse(response);
  }

  static async deleteUser(userId) {
    const response = await this.request(`${API_BASE_URL}/users/${userId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  // ==================== EXTERNAL VIEWER ====================

  static async registerExternalViewer(data) {
    const response = await this.request(`${API_BASE_URL}/external-viewers`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  static async listExternalViewers() {
    const response = await this.request(`${API_BASE_URL}/external-viewers`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  static async revokeExternalViewer(viewerId) {
    const response = await this.request(`${API_BASE_URL}/external-viewers/${viewerId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }
}

export default ApiService;